/**
 * store.ts — Zustand auth store + TokenStore implementation.
 *
 * Tokens live in memory (access) + sessionStorage (refresh) so a tab reload
 * survives but tokens don't persist to disk longer than the browser session.
 * Access tokens are short-lived (15 min, SPEC §2); refresh is 12 h.
 */

import { create } from 'zustand';
import type { AccessClaims, TokenPair } from '@/types';
import type { TokenStore } from '@/api/client';

const REFRESH_KEY = 'helm.refresh';

interface AuthState {
  access: string | null;
  refresh: string | null;
  claims: AccessClaims | null;
  setTokens: (t: TokenPair) => void;
  clear: () => void;
}

function decodeClaims(jwt: string | null): AccessClaims | null {
  if (!jwt) return null;
  const parts = jwt.split('.');
  if (parts.length !== 3) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as AccessClaims;
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  access: null,
  refresh: sessionStorage.getItem(REFRESH_KEY),
  // On a fresh page load `access` is gone (in-memory only) but the refresh token
  // survives in sessionStorage and carries org_id + role + sub — decode it so the
  // UI (role label, gating) is correct immediately, before the first token refresh.
  claims: decodeClaims(sessionStorage.getItem(REFRESH_KEY)),
  setTokens: (t) => {
    sessionStorage.setItem(REFRESH_KEY, t.refresh);
    set({ access: t.access, refresh: t.refresh, claims: decodeClaims(t.access) });
  },
  clear: () => {
    sessionStorage.removeItem(REFRESH_KEY);
    set({ access: null, refresh: null, claims: null });
  },
}));

/** TokenStore adapter handed to ApiClient. */
export const tokenStore: TokenStore = {
  getAccess: () => useAuthStore.getState().access,
  getRefresh: () => useAuthStore.getState().refresh,
  setTokens: (t) => useAuthStore.getState().setTokens(t),
  clear: () => useAuthStore.getState().clear(),
};

export function isAuthenticated(): boolean {
  const { access, refresh } = useAuthStore.getState();
  return Boolean(access || refresh);
}
